/* eslint-disable */
import React from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Header from './Header';
import { toggleWidget, resetApp } from 'redux/actions';
import { AppMessenger } from 'utils';
import { CLOSE_WIDGET, MINIMIZE_MENU } from 'widgetActions';
import { useTranslation } from 'react-i18next';
import { routes } from 'routes';
import { cacheKey, sessionState } from 'appConstants';
import { cache } from 'utils/cache';
import AppDialog from './AppDialog';
// See below comment.
// import { useButtonDemoMode } from 'chatDemoMode/index';

const WidgetHeader = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const { isInChat, isAnonymous, state } = useSelector((state) => state.appReducer);
  const [showModal, setShowModal] = React.useState(false);

  // If app state is set to `failed`, it's likely that
  // there is a connection issue or the token has expired.
  // This will trigger the informative modal to show.
  React.useEffect(() => {
    if (state === sessionState.FAILED) {
      setShowModal(() => true);
    }
  }, [state]);

  // FIXME: `useButtonDemoMode` is causing unexpected behavior and bugs.
  // Since this is a demo mode for the Admin console I am commenting it out.
  // The demo widget should not have logic that could cause issues with the production widget. -Amir
  // const { isDemoMode } = useButtonDemoMode(setShowModal);

  const handleCancel = () => setShowModal(false);

  const handleEndChatConfirmation = () => {
    AppMessenger.sendMessageToWidget({ action: CLOSE_WIDGET });
    cache.clear();

    dispatch(toggleWidget());

    /**
     * Cache current time in ms after user has closed/ended the chat.
     * We will use this timestamp to prevent proactive ARI from
     * automatically re-opening the widget in every new session
     * for the next couple of hours
     * See: https://jira.icims.net/browse/TXTE-17463
     */
    localStorage.setItem(cacheKey.KEEP_WIDGET_CLOSED, Date.now());

    // Delay reset to let the closing animation finish
    setTimeout(() => {
      setShowModal(false);

      routeBackToHome();

      // Clear refresh token interval
      clearInterval(cache.get(cacheKey.REFRESH_TOKEN_INTERVAL_ID));
      // eslint-disable-next-line no-magic-numbers
    }, 500);
  };

  const handleMinimize = () => {
    AppMessenger.sendMessageToWidget({ action: MINIMIZE_MENU });
    dispatch(toggleWidget());
  };

  const handleClose = () => {
    if (isInChat) {
      setShowModal(true);
    } else {
      AppMessenger.sendMessageToWidget({ action: CLOSE_WIDGET });
      dispatch(toggleWidget());

      /**
       * Cache current time in ms after user has closed/ended the chat.
       * We will use this timestamp to prevent proactive ARI from
       * automatically re-opening the widget in every new session
       * for the next couple of hours
       * See: https://jira.icims.net/browse/TXTE-17463
       */
      localStorage.setItem(cacheKey.KEEP_WIDGET_CLOSED, Date.now());
    }
  };

  const routeBackToHome = () => {
    dispatch(resetApp());
    if (isAnonymous) {
      history.push(routes.SPOOFED_THREAD);
    } else {
      history.push(routes.HOME);
    }
  };

  const handleResetChatSession = () => {
    // Close the modal
    setShowModal(() => false);

    // Reset application state
    dispatch(resetApp());

    // Clear refresh token interval
    clearInterval(cache.get(cacheKey.REFRESH_TOKEN_INTERVAL_ID));

    // Clear all cached data
    cache.clear();

    // Reroute user back to home view
    routeBackToHome();
  };

  return (
    <header className="widget-header-container">
      <Header handleMinimize={handleMinimize} handleClose={handleClose} t={t} />
      {/* 
        In addition to `open` props, I am conditionally rendering `AppDialog` because it was
        causing screen flickering when the modal was open and the user selected the button to invoke
        `handleResetChatSession`.
        This can be handled better when we move to RTK, which enables async actions and reducers. -Amir
       */}
      {showModal && (
        <AppDialog
          open={showModal}
          handleMinimize={handleMinimize}
          handleClose={handleClose}
          handleCancel={handleCancel}
          handleEndChatConfirmation={handleEndChatConfirmation}
          handleResetChatSession={handleResetChatSession}
        />
      )}
    </header>
  );
};

export default WidgetHeader;
